import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { authService, impersonationService, User, ApiClientError } from '../api';
import { setTokens } from '../api/client';
import { STORAGE_KEYS } from '../api/config';

type SocialProvider = 'google' | 'microsoft' | 'apple' | 'github';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isImpersonating: boolean;
  isAdmin: boolean;
  canImpersonate: boolean;
  canManageErrorReports: boolean;
  canManageSignInAccess: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string) => Promise<boolean>;
  loginWithSocial: (provider: SocialProvider, idToken?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  clearError: () => void;
  startImpersonation: (userId: string, reason?: string) => Promise<boolean>;
  exitImpersonation: () => Promise<boolean>;
}

const ADMIN_TOKEN_KEY = 'dashboards_admin_access_token';
const ADMIN_REFRESH_TOKEN_KEY = 'dashboards_admin_refresh_token';
const ADMIN_USER_KEY = 'dashboards_admin_user';
const IMPERSONATION_EXPIRY_SKEW_MS = 5_000;

const AuthContext = createContext<AuthContextType | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

function clearStoredAdminSession(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_USER_KEY);
}

/**
 * Restore only the token pair captured before impersonation, then validate its
 * identity server-side. Browser-provided actor IDs are never used.
 */
async function restoreStoredAdminSession(): Promise<User | null> {
  const savedToken = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  const savedRefresh = sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
  if (!savedToken || !savedRefresh) return null;

  try {
    setTokens(savedToken, savedRefresh);
    const adminUser = await authService.getCurrentUser();
    clearStoredAdminSession();
    return adminUser;
  } catch {
    clearStoredAdminSession();
    await authService.logout();
    return null;
  }
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Returns authentication state when mounted, or undefined for anonymous route trees. */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => authService.getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount.
  // Always calls /auth/me — this handles both normal token-based auth
  // and the DEV_USER_ID bypass (backend returns the dev user without a token).
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch {
        // An expired impersonation token has no refresh path. Restore only the
        // separately preserved administrator token pair, then validate it
        // server-side through /auth/me.
        const adminUser = await restoreStoredAdminSession();
        if (adminUser) {
          setUser(adminUser);
        } else {
          await authService.logout();
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authService.login({ email, password });
      setUser(response.user);
      return true;
    } catch (err) {
      setError(err instanceof ApiClientError ? err.error : 'Login failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authService.register({ email, password, name: name || '' });
      setUser(response.user);
      return true;
    } catch (err) {
      setError(err instanceof ApiClientError ? err.error : 'Registration failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithSocial = useCallback(async (provider: SocialProvider, idToken?: string): Promise<boolean> => {
    if (provider === 'google' && idToken) {
      try {
        setIsLoading(true);
        setError(null);
        const response = await authService.loginWithGoogle(idToken);
        setUser(response.user);
        return true;
      } catch (err) {
        setError(err instanceof ApiClientError ? err.error : 'Google login failed. Please try again.');
        return false;
      } finally {
        setIsLoading(false);
      }
    }

    if (provider === 'microsoft' && idToken) {
      try {
        setIsLoading(true);
        setError(null);
        const response = await authService.loginWithMicrosoft(idToken);
        setUser(response.user);
        return true;
      } catch (err) {
        setError(err instanceof ApiClientError ? err.error : 'Microsoft login failed. Please try again.');
        return false;
      } finally {
        setIsLoading(false);
      }
    }

    setError(`${provider} login is not yet supported.`);
    return false;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      // Clear any saved admin tokens too
      clearStoredAdminSession();
      await authService.logout();
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startImpersonation = useCallback(async (userId: string, reason?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // A complete original token pair is required because impersonation tokens
      // have no refresh path and can only return safely to the preserved session.
      const currentAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const currentRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!currentAccessToken || !currentRefreshToken) {
        clearStoredAdminSession();
        setError('Your administrator session is incomplete. Please sign in again.');
        return false;
      }

      // Preserve the validated pair only after the precondition succeeds.
      const currentUser = localStorage.getItem(STORAGE_KEYS.USER);
      sessionStorage.setItem(ADMIN_TOKEN_KEY, currentAccessToken);
      sessionStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, currentRefreshToken);
      if (currentUser) sessionStorage.setItem(ADMIN_USER_KEY, currentUser);

      // Start impersonation — get impersonation token
      const result = await impersonationService.startImpersonation(userId, reason);

      // Set impersonation token (no refresh token for impersonation)
      setTokens(result.accessToken, '');

      // Reload user profile (will now include impersonation metadata)
      const impersonatedUser = await authService.getCurrentUser();
      setUser(impersonatedUser);

      return true;
    } catch (err) {
      // Restore the original signed admin session and remove the temporary copy.
      const savedToken = sessionStorage.getItem(ADMIN_TOKEN_KEY);
      const savedRefresh = sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
      if (savedToken && savedRefresh) {
        setTokens(savedToken, savedRefresh);
      }
      clearStoredAdminSession();
      setError(err instanceof ApiClientError ? err.error : 'Failed to start impersonation');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const exitImpersonation = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);

      // The server persists the mandatory end audit event before admin mode is
      // restored. If it fails, remain in impersonation mode and surface error.
      await impersonationService.endImpersonation();

      const adminUser = await restoreStoredAdminSession();
      if (adminUser) {
        setUser(adminUser);
      } else {
        // No valid original administrator session remains.
        await authService.logout();
        setUser(null);
      }

      setError(null);
      return adminUser !== null;
    } catch (err) {
      console.error('Exit impersonation error:', err);
      setError(
        err instanceof ApiClientError
          ? err.error
          : 'Failed to exit impersonation safely',
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const expiresAt = user?.impersonation?.expiresAt;
    if (!expiresAt) return;

    const expiresAtMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return;

    let expiryFallbackTimer: number | undefined;
    const auditedExitDelay = Math.max(
      0,
      expiresAtMs - Date.now() - IMPERSONATION_EXPIRY_SKEW_MS,
    );

    const auditedExitTimer = window.setTimeout(async () => {
      const exited = await exitImpersonation();
      if (exited) return;

      // A failed early audit must not switch identities while the signed
      // impersonation token remains valid. Once it expires, restore and
      // validate the preserved administrator session as the safe fallback.
      const expiryFallbackDelay = Math.max(0, expiresAtMs - Date.now());
      expiryFallbackTimer = window.setTimeout(async () => {
        setIsLoading(true);
        try {
          const adminUser = await restoreStoredAdminSession();
          setUser(adminUser);
          if (!adminUser) {
            await authService.logout();
          }
        } finally {
          setIsLoading(false);
        }
      }, expiryFallbackDelay);
    }, auditedExitDelay);

    return () => {
      window.clearTimeout(auditedExitTimer);
      if (expiryFallbackTimer !== undefined) {
        window.clearTimeout(expiryFallbackTimer);
      }
    };
  }, [exitImpersonation, user?.impersonation?.expiresAt]);

  const updateUser = useCallback((updatedFields: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const merged = { ...prev, ...updatedFields };
      authService.storeUser(merged);
      return merged;
    });
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const isImpersonating = user?.impersonation?.active === true;
  const isAdmin = user?.role === 'admin';
  const canImpersonate =
    isAdmin &&
    !isImpersonating &&
    user?.isActive !== false &&
    user?.permissions?.includes('admin.impersonate') === true;
  const canManageErrorReports =
    isAdmin &&
    !isImpersonating &&
    user?.isActive !== false &&
    user?.permissions?.includes('admin.error-reports.manage') === true;
  const canManageSignInAccess =
    isAdmin &&
    !isImpersonating &&
    user?.isActive !== false &&
    user?.permissions?.includes('admin.sign-in-access.manage') === true;

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    isImpersonating,
    isAdmin,
    canImpersonate,
    canManageErrorReports,
    canManageSignInAccess,
    login,
    register,
    loginWithSocial,
    logout,
    updateUser,
    clearError,
    startImpersonation,
    exitImpersonation,
  }), [user, isLoading, error, isImpersonating, isAdmin, canImpersonate, canManageErrorReports, canManageSignInAccess, login, register, loginWithSocial, logout, updateUser, clearError, startImpersonation, exitImpersonation]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};